import { useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CircleCheck, TriangleAlert, Upload } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Field, FieldLabel } from "@/components/ui/field";
import {
  Popover,
  PopoverContent,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { queryClient } from "App";
import { formatError } from "helpers/formatError";
import { useMetaLeadsImportJob } from "hooks/useMetaLeadsImportJob";
import Attachment from "services/Attachment";
import MetaDatasetService from "services/MetaDataset";
import MetaLeadService from "services/MetaLead";

const MAX_BYTES = 25 * 1024 * 1024;

// Sentinel for the "no dataset" choice, since a Select item cannot be empty.
const NO_DATASET = "__none__";

const formatBytes = bytes => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const validateFile = file => {
  if (!file.name?.toLowerCase().endsWith(".csv")) {
    return "That file is not a CSV. Please choose a .csv file.";
  }
  if (file.size > MAX_BYTES) {
    return `File too large (${formatBytes(file.size)}). The 25 MB limit applies.`;
  }
  return null;
};

const SampleTable = ({ sample }) => (
  <div className="tw:overflow-x-auto">
    <table className="tw:w-full tw:border-collapse tw:text-sm">
      <thead>
        <tr className="tw:text-left tw:text-[var(--color-text-secondary)]">
          {["Name", "Email", "Phone", "Campaign", "Form"].map(header => (
            <th
              key={header}
              className="tw:border-b tw:border-[var(--color-border)] tw:px-2 tw:py-1.5 tw:font-medium"
            >
              {header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sample.map((row, index) => (
          <tr key={index}>
            {[
              row.name,
              row.email,
              row.phone,
              row.campaign_name,
              row.form_name,
            ].map((cell, cellIndex) => (
              <td
                key={cellIndex}
                className="tw:border-b tw:border-[var(--color-border)] tw:px-2 tw:py-1.5 tw:text-[var(--color-text-primary)]"
              >
                {cell || (
                  <span className="tw:text-[var(--color-text-muted)]">—</span>
                )}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ColumnGroup = ({ title, names }) => {
  if (!names.length) return null;
  return (
    <div className="tw:flex tw:flex-col tw:gap-1.5">
      <span className="tw:text-sm tw:font-medium tw:text-[var(--color-text-secondary)]">
        {title}
      </span>
      <div className="tw:flex tw:flex-wrap tw:gap-1.5">
        {names.map(name => (
          <Badge key={name} variant="secondary">
            {name}
          </Badge>
        ))}
      </div>
    </div>
  );
};

const ImportLeadsTab = () => {
  const { accountId } = useParams();
  const [uploaded, setUploaded] = useState(null);
  const [progress, setProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [datasetId, setDatasetId] = useState(NO_DATASET);
  const [dataJobId, setDataJobId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { data: datasets = [] } = useQuery({
    queryKey: ["meta-datasets", accountId],
    queryFn: async () => {
      const result = await MetaDatasetService.list(accountId);
      return result.data ?? [];
    },
  });

  const previewMutation = useMutation({
    mutationFn: payload => MetaLeadService.previewImport(accountId, payload),
  });

  const importMutation = useMutation({
    mutationFn: payload => MetaLeadService.import(accountId, payload),
    onSuccess: result => {
      setDataJobId(result.data_job_id);
    },
  });

  const importJob = useMetaLeadsImportJob(dataJobId);

  const reset = () => {
    setUploaded(null);
    setUploadError(null);
    setProgress(0);
    setDataJobId(null);
    setDatasetId(NO_DATASET);
    previewMutation.reset();
    importMutation.reset();
    queryClient.invalidateQueries({ queryKey: ["meta-leads", accountId] });
  };

  const runImport = () => {
    const payload = { ...uploaded };
    if (datasetId !== NO_DATASET) {
      payload.dataset_id = datasetId;
    }
    importMutation.mutate(payload);
  };

  const handleSelect = async event => {
    setUploadError(null);
    const file = event.target.files?.[0];
    if (!file) return;

    const validationError = validateFile(file);
    if (validationError) {
      setUploadError(validationError);
      return;
    }

    setIsUploading(true);
    setProgress(0);
    try {
      const result = await Attachment.uploadFile(file, evt => {
        if (evt.total) {
          setProgress(Math.round((evt.loaded / evt.total) * 100));
        }
      });
      const payload = {
        s3_key: result.s3_key,
        file_name: result.file_name,
      };
      setUploaded(payload);
      await previewMutation.mutateAsync(payload);
    } catch (err) {
      setUploadError(formatError(err, "Upload failed. Please try again."));
    } finally {
      setIsUploading(false);
    }
  };

  const preview = previewMutation.data;

  if (dataJobId) {
    const { progress: jobProgress, isComplete, isFailed } = importJob;

    if (isFailed) {
      return (
        <div className="tw:flex tw:w-full tw:flex-col tw:gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="tw:flex tw:items-center tw:gap-2">
                <TriangleAlert className="tw:text-destructive" />
                Import failed
              </CardTitle>
              <CardDescription>
                The import could not be completed. Please try again.
              </CardDescription>
            </CardHeader>
          </Card>
          <div>
            <Button variant="outline" onClick={reset}>
              Import Another File
            </Button>
          </div>
        </div>
      );
    }

    if (isComplete && jobProgress) {
      return (
        <div className="tw:flex tw:w-full tw:flex-col tw:gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="tw:flex tw:items-center tw:gap-2">
                <CircleCheck className="tw:text-[var(--color-teal)]" />
                Import complete
              </CardTitle>
              <CardDescription>
                {jobProgress.imported} added, {jobProgress.updated} updated,{" "}
                {jobProgress.skipped} skipped of {jobProgress.row_count} rows.
                {jobProgress.campaign_linked > 0 &&
                  ` ${jobProgress.campaign_linked} lead${
                    jobProgress.campaign_linked === 1 ? "" : "s"
                  } linked to campaigns.`}
              </CardDescription>
            </CardHeader>
            {jobProgress.errors?.length > 0 && (
              <CardContent className="tw:flex tw:flex-col tw:gap-2">
                <span className="tw:text-sm tw:font-medium tw:text-[var(--color-text-secondary)]">
                  Skipped rows
                </span>
                <div className="tw:overflow-x-auto">
                  <table className="tw:w-full tw:border-collapse tw:text-sm">
                    <thead>
                      <tr className="tw:text-left tw:text-[var(--color-text-secondary)]">
                        <th className="tw:border-b tw:border-[var(--color-border)] tw:px-2 tw:py-1.5 tw:font-medium">
                          Row
                        </th>
                        <th className="tw:border-b tw:border-[var(--color-border)] tw:px-2 tw:py-1.5 tw:font-medium">
                          Reason
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobProgress.errors.map(error => (
                        <tr key={error.row}>
                          <td className="tw:border-b tw:border-[var(--color-border)] tw:px-2 tw:py-1.5">
                            {error.row}
                          </td>
                          <td className="tw:border-b tw:border-[var(--color-border)] tw:px-2 tw:py-1.5">
                            {error.message}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            )}
          </Card>
          <div>
            <Button variant="outline" onClick={reset}>
              Import Another File
            </Button>
          </div>
        </div>
      );
    }

    const total = jobProgress?.total_rows ?? 0;
    const current = jobProgress?.current_row ?? 0;
    const percent = total ? Math.round((current / total) * 100) : 0;
    return (
      <div className="tw:flex tw:w-full tw:flex-col tw:gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="tw:flex tw:items-center tw:gap-2">
              <Spinner />
              Importing leads
            </CardTitle>
            <CardDescription>
              {total
                ? `Processed ${current} of ${total} rows.`
                : "Preparing import…"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="tw:h-2 tw:w-full tw:overflow-hidden tw:rounded-full tw:bg-[var(--color-warm-gray)]">
              <div
                className="tw:h-full tw:rounded-full tw:bg-[var(--color-highlight)] tw:transition-all"
                style={{ width: `${percent}%` }}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (preview) {
    const mapped = preview.columns
      .filter(column => column.destination === "column")
      .map(column => column.name);
    const fieldData = preview.columns
      .filter(column => column.destination === "field_data")
      .map(column => column.name);

    return (
      <div className="tw:flex tw:w-full tw:flex-col tw:gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Confirm import</CardTitle>
            <CardDescription>
              {preview.row_count} lead
              {preview.row_count === 1 ? "" : "s"} found in{" "}
              {uploaded?.file_name}.
            </CardDescription>
          </CardHeader>
          <CardContent className="tw:flex tw:flex-col tw:gap-5">
            <div className="tw:flex tw:flex-col tw:gap-3">
              <ColumnGroup title="Mapped to columns" names={mapped} />
              <ColumnGroup title="Stored in field data" names={fieldData} />
            </div>
            {preview.sample?.length > 0 && (
              <div className="tw:flex tw:flex-col tw:gap-2">
                <span className="tw:text-sm tw:font-medium tw:text-[var(--color-text-secondary)]">
                  Preview
                </span>
                <SampleTable sample={preview.sample} />
              </div>
            )}
            <Field>
              <FieldLabel htmlFor="import-dataset">
                Report qualified leads to
              </FieldLabel>
              <Select value={datasetId} onValueChange={setDatasetId}>
                <SelectTrigger id="import-dataset" className="tw:w-80">
                  <SelectValue>
                    {value =>
                      value === NO_DATASET
                        ? "Do not report to Meta"
                        : (datasets.find(d => d.dataset_id === value)?.name ??
                          value)
                    }
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_DATASET}>
                    Do not report to Meta
                  </SelectItem>
                  {datasets.map(dataset => (
                    <SelectItem key={dataset.id} value={dataset.dataset_id}>
                      {dataset.name || dataset.dataset_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="tw:text-sm tw:text-[var(--color-text-muted)]">
                With a dataset, qualifying an imported lead reports a Lead event
                to it via the Meta Conversions API. Without one, leads are
                imported only.
              </span>
            </Field>
          </CardContent>
        </Card>
        {importMutation.isError && (
          <div className="tw:flex tw:items-center tw:gap-2 tw:rounded-md tw:border tw:border-[var(--color-border)] tw:px-4 tw:py-3 tw:text-sm tw:text-destructive">
            <TriangleAlert className="tw:max-h-4" />
            {formatError(
              importMutation.error,
              "Import failed. Please try again."
            )}
          </div>
        )}
        <div className="tw:flex tw:gap-2">
          <Popover
            open={confirmOpen}
            onOpenChange={open => {
              // A chosen dataset imports straight away; only a missing one needs
              // the operator to confirm they mean to skip Conversions reporting.
              if (open && datasetId !== NO_DATASET) {
                runImport();
                return;
              }
              setConfirmOpen(open);
            }}
          >
            <PopoverTrigger
              className={buttonVariants()}
              disabled={importMutation.isPending}
            >
              {importMutation.isPending && <Spinner />}
              Import
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className="tw:w-80 tw:bg-[var(--color-surface-overlay)] tw:text-[var(--color-text-primary)]"
            >
              <PopoverHeader>
                <PopoverTitle>Import without a dataset?</PopoverTitle>
              </PopoverHeader>
              <span className="tw:text-sm tw:text-[var(--color-text-secondary)]">
                No dataset is selected, so qualifying these leads will not
                report a Lead event to Meta. Choose a dataset above to enable
                reporting.
              </span>
              <div className="tw:flex tw:gap-2">
                <Button
                  onClick={() => {
                    setConfirmOpen(false);
                    runImport();
                  }}
                >
                  Import Anyway
                </Button>
                <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
                  Cancel
                </Button>
              </div>
            </PopoverContent>
          </Popover>
          <Button
            variant="ghost"
            onClick={reset}
            disabled={importMutation.isPending}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="tw:flex tw:w-full tw:flex-col tw:gap-4">
      <div>
        <h2 className="tw:m-0 tw:font-heading tw:text-2xl tw:text-[var(--color-text-primary)]">
          Import leads
        </h2>
        <p className="tw:m-0 tw:text-[var(--color-text-secondary)]">
          Upload a Meta Lead Ads CSV export. Its leads land alongside synced
          leads and dedup on the Meta lead id.
        </p>
      </div>

      <label
        htmlFor="meta-leads-csv-input"
        className="tw:flex tw:cursor-pointer tw:flex-col tw:items-center tw:gap-2 tw:rounded-lg tw:border-2 tw:border-dashed tw:border-[var(--color-border)] tw:bg-[var(--color-surface)] tw:p-12 tw:text-center"
      >
        <Upload className="tw:max-h-6 tw:text-[var(--color-highlight)]" />
        <span className="tw:font-heading tw:font-semibold tw:text-[var(--color-highlight)]">
          Choose a CSV to upload
        </span>
        <span className="tw:text-sm tw:text-[var(--color-text-muted)]">
          Exported from Meta Ads Manager
        </span>
        <input
          id="meta-leads-csv-input"
          type="file"
          accept=".csv"
          onChange={handleSelect}
          disabled={isUploading || previewMutation.isPending}
          className="tw:sr-only"
        />
      </label>

      {(isUploading || previewMutation.isPending) && (
        <div className="tw:flex tw:items-center tw:gap-2 tw:text-sm tw:text-[var(--color-text-secondary)]">
          <Spinner />
          {isUploading ? `Uploading… ${progress}%` : "Parsing…"}
        </div>
      )}

      {uploadError && (
        <div className="tw:flex tw:items-center tw:gap-2 tw:rounded-md tw:border tw:border-[var(--color-border)] tw:px-4 tw:py-3 tw:text-sm tw:text-destructive">
          <TriangleAlert className="tw:max-h-4" />
          {uploadError}
        </div>
      )}
    </div>
  );
};

export default ImportLeadsTab;
